'use client';
import useSWR, { mutate } from "swr";
import { useSession } from "next-auth/react";

interface User {
  full_name: string;
  email: string;
  phoneNumber: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  tax: string;
  is_payed: boolean;
}

const fetcher = async <T>(url: string): Promise<T> => {
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Token ${process.env.NEXT_PUBLIC_TOKEN}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error("Failed to fetch data. Please try again later.");
  }
};

const useFetcherUser = () => {
  const { data: session } = useSession();
  const key = session?.user?.id
    ? `${process.env.NEXT_PUBLIC_URL}infoid/${session.user.id}`
    : null;

  const { data, error, isLoading } = useSWR<User | null>(key, fetcher<User>);

  const revalidate = (optimisticData?: Partial<User>) => {
    if (!key) return;
    if (optimisticData) {
      const updater = (current: User | null | undefined): User | null => {
        if (!current) return null;
        return { ...current, ...optimisticData } as User;
      };
      mutate<User | null>(key, updater, { revalidate: true });
    } else {
      mutate(key);
    }
  };

  // ✅ Expose mutateUser for direct SWR cache revalidation
  const mutateUser = () => {
    if (!key) return;
    mutate(key);
  };

  return { userinfo: data, error, isLoading, revalidate, mutateUser, mutate: mutateUser };
};

export default useFetcherUser;